import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Upload, Trash2, Send, CheckCircle } from 'lucide-react';
import { useSubmitFeedback } from '@/services/feedback/hooks';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

type FeedbackMutation = Pick<ReturnType<typeof useSubmitFeedback>, 'mutate' | 'isPending' | 'data'>;

const FeedbackDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  transactionId?: string | null;
}> = ({ isOpen, onClose, transactionId }) => (
  <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-w-[500px]">
      {isOpen && <LiveFeedbackDialogContent onClose={onClose} transactionId={transactionId} />}
    </DialogContent>
  </Dialog>
);

const LiveFeedbackDialogContent: React.FC<{
  onClose: () => void;
  transactionId?: string | null;
}> = (props) => <FeedbackDialogContent {...props} submitFeedbackMutation={useSubmitFeedback()} />;

export const FeedbackDialogContent: React.FC<{
  onClose: () => void;
  transactionId?: string | null;
  submitFeedbackMutation: FeedbackMutation;
}> = ({ onClose, transactionId, submitFeedbackMutation }) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [includeSourceDocument, setIncludeSourceDocument] = useState(!!transactionId);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }

      setScreenshot(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = () => {
    // Validate
    if (feedbackText.trim().length < 10) {
      setError('Please provide at least 10 characters of feedback');
      return;
    }

    setError('');

    submitFeedbackMutation.mutate(
      {
        feedbackText,
        screenshot,
        transactionId,
        includeSourceDocument,
      },
      {
        onSuccess: () => {
          setSuccess(true);
          closeTimer.current = setTimeout(onClose, 3000);
        },
        onError: (err: unknown) => {
          console.error('Error submitting feedback:', err);
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to submit feedback. Please try again.';
          setError(errorMessage);
        },
      }
    );
  };

  const isSubmitting = submitFeedbackMutation.isPending;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <MessageSquare className="size-5" />
          Send Feedback
        </DialogTitle>
      </DialogHeader>

      {success ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="text-primary mb-4">
            <CheckCircle className="size-12" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Thank you!</h3>
          <p className="text-muted-foreground">
            {submitFeedbackMutation.data?.reference
              ? `Your feedback has been logged as ticket ${submitFeedbackMutation.data.reference}.`
              : 'Your feedback has been submitted successfully.'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4 py-2">
            {/* Feedback text area */}
            <div className="space-y-2">
              <Label htmlFor="feedback-text">Your Feedback</Label>
              <Textarea
                id="feedback-text"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Tell us what you think, report an issue, or suggest an improvement..."
                rows={6}
                maxLength={5000}
                disabled={isSubmitting}
                className="resize-none"
              />
              <div className="text-xs text-right">
                <span
                  className={
                    feedbackText.length < 10 ? 'text-destructive' : 'text-muted-foreground'
                  }
                >
                  {feedbackText.length < 10
                    ? `${10 - feedbackText.length} more characters required`
                    : `${feedbackText.length}/5000 characters`}
                </span>
              </div>
            </div>

            {/* Screenshot upload */}
            <div className="space-y-2">
              <Label>Screenshot (optional)</Label>

              {screenshotPreview ? (
                <div className="relative border border-border rounded-md overflow-hidden">
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    className="w-full max-h-[200px] object-contain bg-muted"
                  />
                  <button
                    className="absolute top-2 right-2 p-1.5 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors"
                    onClick={handleRemoveScreenshot}
                    disabled={isSubmitting}
                    title="Remove screenshot"
                  >
                    <Trash2 className="size-4" />
                  </button>
                  <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
                    {screenshot?.name}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="flex w-full flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-border rounded-md cursor-pointer hover:border-primary hover:bg-accent/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                >
                  <Upload className="size-6 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Click to upload a screenshot
                  </span>
                  <span className="text-xs text-muted-foreground">PNG, JPG, GIF up to 5MB</span>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={isSubmitting}
              />
            </div>

            {transactionId && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="feedback-include-source"
                  checked={includeSourceDocument}
                  onCheckedChange={(checked) => setIncludeSourceDocument(checked === true)}
                  disabled={isSubmitting}
                />
                <Label
                  htmlFor="feedback-include-source"
                  className="text-sm font-normal cursor-pointer"
                >
                  Include the source document with this feedback
                </Label>
              </div>
            )}

            {error && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting || feedbackText.length < 10}>
              {isSubmitting ? (
                'Sending...'
              ) : (
                <>
                  <Send className="size-4" />
                  Send Feedback
                </>
              )}
            </Button>
          </DialogFooter>
        </>
      )}
    </>
  );
};

export default FeedbackDialog;
